import React from 'react'
import MaterialIcon from 'react-native-vector-icons/MaterialIcons'
import moment from 'moment'
import I18n from 'APP/I18n'
import Typography from 'APP/Converse/Typography'
import { Colors, Metrics } from 'APP/Themes'
import { Constants } from 'APP/Components/ActiveMinutes'
import {
  ActivityIconStyle,
  ActivityRow,
  ActivityTypeGroup,
  Card,
  CheckmarkStyle,
  CurrentGroup,
  CurrentProgress,
  DateRange,
  NoActivities,
  ProgressBar,
  ProgressRow,
  WeeklyProgressLabel,
} from './style'

type ActivityType = (typeof Constants.ACTIVITY_TYPE)[keyof typeof Constants.ACTIVITY_TYPE]

interface Activity {
  activity: ActivityType
  minutes: number
}

interface HistoricalWeeklySummaryProps {
  startDate?: string
  endDate?: string
  weeklyGoal?: number
  weeklyProgress?: number
  activities?: Activity[]
}

const HistoricalWeeklySummary: React.FC<HistoricalWeeklySummaryProps> = ({
  startDate = '',
  endDate = '',
  weeklyGoal = 0,
  weeklyProgress = 0,
  activities = [],
}) => {
  const achieved = weeklyProgress / weeklyGoal >= 1

  const datesAreSameMonth = moment(startDate).format('MMM') === moment(endDate).format('MMM')
  const dateRangeLabel = `${
    datesAreSameMonth ? moment(startDate).format('D') : moment(startDate).format('D MMM')
  } - ${moment(endDate).format('D MMM')}`
  const weeklyProgressLabel = `${weeklyProgress} ${I18n.t('RecentActiveHistoryScreen.min')}`

  return (
    <Card testID="historicalWeeklySummary">
      {startDate && endDate && (
        <DateRange variant="bodySmallBold" color="textSecondary" testID="dateRange">
          {dateRangeLabel}
        </DateRange>
      )}
      <ProgressRow>
        <CurrentGroup>
          {achieved && (
            <MaterialIcon
              name="check"
              size={Metrics.icons.small}
              color={Colors.habitDoneAccent}
              style={CheckmarkStyle}
            />
          )}
          <WeeklyProgressLabel variant="h3" color="accent" achieved={achieved}>
            {weeklyProgressLabel}
          </WeeklyProgressLabel>
        </CurrentGroup>
        <Typography variant="h4" color="text">
          {weeklyGoal} {I18n.t('RecentActiveHistoryScreen.min')}
        </Typography>
      </ProgressRow>
      <ProgressBar>
        <CurrentProgress ratio={weeklyProgress / weeklyGoal} achieved={achieved} />
      </ProgressBar>
      {(activities || []).length === 0 && (
        <NoActivities
          testID="noActivities"
          variant="bodySmallBold"
          color="textSecondary"
          align="center"
        >
          {I18n.t('RecentActiveHistoryScreen.emptyStates.noActivitiesForWeek')}
        </NoActivities>
      )}
      {activities?.map(({ activity, minutes }) => {
        const Icon = Constants.ACTIVITY_ICON[activity]

        return (
          <ActivityRow testID="activity" key={`activity-${activity}-${minutes}`}>
            <ActivityTypeGroup>
              <Icon.Icon
                name={Icon.name}
                size={Metrics.icons.small}
                color={Colors.text}
                style={ActivityIconStyle}
              />
              <Typography variant="bodyNormal" color="text">
                {I18n.t(`RecentActiveHistoryScreen.activity.${activity}`)}
              </Typography>
            </ActivityTypeGroup>
            <Typography variant="bodySmallBold" color="text">
              {minutes} {I18n.t('RecentActiveHistoryScreen.min')}
            </Typography>
          </ActivityRow>
        )
      })}
    </Card>
  )
}

export default HistoricalWeeklySummary
